﻿<template>
  <div>
    <div class="p-6 pb-0">
      <BreadCrumbs :navigation="navigation" />
    </div>

    <ConfirmationModal :show="confirmDeleteShow" @close="confirmDeleteShow = false">
        <template #title>Conferma Eliminazione Riga contratto</template>

        <template #content>
            Sei sicuro di voler eliminare <strong>{{ rowDeletion.Item }}</strong> su {{ form.DocNo }} di {{ form.Customer.name }}?
        </template>

        <template #footer>
            <button 
                @click="confirmDeleteShow = false;" 
                class="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600 mr-2"
            >
                Annulla
            </button>
            <button 
                @click="deleteLine(this.contractLines.indexOf(rowDeletion)); confirmDeleteShow = false;" 
                class="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600"
            >
                Conferma
            </button>
        </template>
    </ConfirmationModal>


    <form @submit.prevent="submitForm" autocomplete="nope">
      <div class="px-6 pt-6 pb-6 contract-details-section">
        <!-- Accordion Section 1: Dettagli Contratto -->
        <div class="mb-4 bg-white rounded-md shadow-lg">
          <div 
            @click="toggleSection('contractDetails')" 
            class="flex justify-between items-center px-6 py-3 cursor-pointer bg-gray-100 rounded-t-md"
          >
            <h3 class="font-semibold">{{ pageTitle }}</h3>
            <span class="transform transition-transform" :class="{'rotate-180': !sections.contractDetails}">
              &#9660;<!-- Black down arrow Unicode character -->
            </span>
          </div>
          <div v-show="sections.contractDetails" class="px-2 py-2">
            <div v-if="errors.general" class="pl-4 text-red-800">{{ errors.general }}</div>
            <div class="grid grid-cols-3">
              <div class="p-4">
                <div class="mb-4">
                  <label class="block text-gray-700">Codice *</label>
                  <input v-model="form.DocNo" type="text" class="p93__input w-full" required autocomplete="nope">
                  <div v-if="errors.code" class="py-4 text-red-800">{{ errors.code }}</div>
                </div>
                <div class="mb-4">
                  <label class="block text-gray-700">Tipo contratto *</label>
                  <v-select 
                      :options="contractTypes"
                      :reduce="type => type.value"
                      v-model="form.ContractType"
                      label="name"
                      placeholder="Seleziona tipo contratto..." 
                      class="w-full"
                      :disabled="!form.Customer"
                  >
                  </v-select>
                  <div v-if="errors.ContractType" class="py-4 text-red-800">{{ errors.ContractType }}</div>
                </div>
                <div class="mb-4">
                  <label class="block text-gray-700">Modello *</label>
                  <v-select 
                      :options="contractModels"
                      v-model="form.Model"
                      label="name"
                      placeholder="Seleziona modello contratto..." 
                      class="w-full"
                      :reduce="model => model.id"
                      :disabled="!form.Customer"
                  >
                  </v-select>
                  <div v-if="errors.Model" class="py-4 text-red-800">{{ errors.Model }}</div>
                </div>
                <div class="mb-4">
                  <label class="block text-gray-700">Commessa</label>
                  <v-select 
                      :options="jobs"
                      v-model="form.Job"
                      label="name"
                      placeholder="Seleziona commessa..." 
                      class="w-full"
                      :reduce="job => job.id"
                      :disabled="!form.Customer"
                  >
                  </v-select>
                  <div v-if="errors.Job" class="py-4 text-red-800">{{ errors.Job }}</div>
                </div>
                <div class="mb-4">
                  <label class="block text-gray-700">Condizioni di pagamento</label>
                  <v-select 
                      :options="paymentTerms"
                      v-model="form.PaymentCondition"
                      label="name"
                      placeholder="Seleziona condizioni di pagamento..." 
                      class="w-full"
                      :reduce="paymentTerm => paymentTerm.id"
                      :disabled="!form.Customer"
                  >
                  </v-select>
                  <div v-if="errors.PaymentCondition" class="py-4 text-red-800">{{ errors.PaymentCondition }}</div>
                </div>
              </div>
              <div class="p-4">
                <div class="mb-4">
                  <label class="block text-gray-700">Cliente *</label>
                  <v-select :options="foundClients" :filterable="false" v-model="form.Customer" label="name"
                    @search="searchClients" @option:selected="onClientSelect" required>
                    <template #no-options="{ search, searching }">
                      <div v-if="searching">🔍 Ricerca in corso...</div>
                      <div v-else-if="!search">Inizia a digitare per cercare un cliente...</div>
                    </template>
                  </v-select>
                  <div v-if="errors.Customer" class="py-4 text-red-800">{{ errors.Customer }}</div>
                </div>
                <div class="mb-4">
                  <label class="block text-gray-700">Frequenza fatturazione *</label>
                  <v-select 
                      :options="billingPeriods"
                      v-model="form.BillingPeriod"
                      label="name"
                      placeholder="Seleziona frequenza fatturazione..." 
                      class="w-full"
                      :reduce="billingPeriod => billingPeriod.id"
                      :disabled="!form.Customer"
                  >
                  </v-select>
                  <div v-if="errors.BillingPeriod" class="py-4 text-red-800">{{ errors.BillingPeriod }}</div>
                </div>
                <div class="mb-4">
                  <label class="block text-gray-700">Data stipula *</label>
                  <VueDatePicker v-model="form.StipulationDate" locale="it" format="dd/MM/yyyy" auto-apply
                            :enable-time-picker="false" text-input
                            class="w-full border border-neutral-300 rounded-md shadow-md" @input="pageDateInput"/>
                  <div v-if="errors.StipulationDate" class="py-4 text-red-800">{{ errors.StipulationDate }}</div>
                </div>
                <div class="mb-4">
                  <label class="block text-gray-700">Agente</label>
                  <v-select 
                      :options="agents"
                      v-model="form.SalePerson"
                      label="name"
                      placeholder="Seleziona agente..." 
                      class="w-full"
                      :reduce="agent => agent.id"
                      :disabled="!form.Customer"
                  >
                  </v-select>
                  <div v-if="errors.SalePerson" class="py-4 text-red-800">{{ errors.SalePerson }}</div>
                </div>
                <div class="mb-4">
                  <label class="block text-gray-700">Listino</label>
                  <v-select 
                      :options="priceLists"
                      v-model="form.PriceList"
                      label="name"
                      placeholder="Seleziona listino..." 
                      class="w-full"
                      :reduce="priceList => priceList.id"
                      :disabled="!form.Customer"
                  >
                  </v-select>
                  <div v-if="errors.priceList" class="py-4 text-red-800">{{ errors.priceList }}</div>
                </div>
              </div>
              <div class="p-4">
                <div class="mb-4">
                  <label class="block text-gray-700">Sede contratto</label>
                  <select v-model="form.ContractPlace" class="p93__input w-full" :disabled="!form.Customer">
                    <option value="" selected="selected">Sede principale</option>
                    <option v-for="site in sites" :key="site.id" :value="site.id">{{ site.name }}</option>
                  </select>
                  <div v-if="errors.ContractPlace" class="py-4 text-red-800">{{ errors.ContractPlace }}</div>
                </div>
                <div class="mb-4">
                  <label class="block text-gray-700">Sede utilizzatore</label>
                  <select v-model="form.UtilizerPlace" class="p93__input w-full" :disabled="!form.Customer">
                    <option value="" selected="selected">Sede principale</option>
                    <option v-for="site in sites" :key="site.id" :value="site.id">{{ site.name }}</option>
                  </select>
                  <div v-if="errors.UtilizerPlace" class="py-4 text-red-800">{{ errors.UtilizerPlace }}</div>
                </div>
                <div class="mb-4">
                  <label class="block text-gray-700">Ultima revisione</label>
                  <VueDatePicker v-model="form.LastRevision" locale="it" format="dd/MM/yyyy" auto-apply
                            :enable-time-picker="false" text-input
                            class="w-full border border-neutral-300 rounded-md shadow-md" @input="pageDateInput"/>
                  <div v-if="errors.LastRevision" class="py-4 text-red-800">{{ errors.LastRevision }}</div>
                </div>
                <div class="mb-4">
                  <label class="block text-gray-700">Capo area</label>
                  <v-select 
                      :options="areaManagers"
                      v-model="form.AreaManager"
                      label="name"
                      placeholder="Seleziona capo area..." 
                      class="w-full"
                      :reduce="areaManager => areaManager.id"
                      :disabled="!form.Customer"
                  >
                  </v-select>
                  <div v-if="errors.areaManager" class="py-4 text-red-800">{{ errors.areaManager }}</div>
                </div>
                <div class="mb-4">
                  <label for="SignedContract" class="flex items-center cursor-pointer">
                    <input id="SignedContract" type="checkbox" v-model="form.SignedContract" class="sr-only" />
                    <div class="relative w-10 h-5 bg-gray-300 rounded-full transition-colors duration-200"
                      :class="{ 'bg-green-500': form.SignedContract }">
                      <span
                        class="absolute left-0 top-0 h-5 w-5 bg-white rounded-full shadow transform transition-transform duration-200"
                        :class="{ 'translate-x-5': form.SignedContract }" />
                    </div>
                    <span class="ml-3 text-gray-700">Contratto firmato</span>
                  </label>
                  <div v-if="errors.SignedContract" class="py-4 text-red-800">{{ errors.SignedContract }}</div>
                </div>
                <div class="mb-4">
                  <label for="contractDisabled" class="flex items-center cursor-pointer">
                    <input id="contractDisabled" type="checkbox" v-model="form.Disabled" class="sr-only" />
                    <div class="relative w-10 h-5 bg-gray-300 rounded-full transition-colors duration-200"
                      :class="{ 'bg-red-500': form.Disabled }">
                      <span
                        class="absolute left-0 top-0 h-5 w-5 bg-white rounded-full shadow transform transition-transform duration-200"
                        :class="{ 'translate-x-5': form.Disabled }" />
                    </div>
                    <span class="ml-3 text-gray-700">Disabilitato</span>
                  </label>
                  <div v-if="errors.Disabled" class="py-4 text-red-800">{{ errors.Disabled }}</div>
                </div>
              </div>
              <div class="px-4 col-span-3">
                <div class="mb-4">
                  <label class="block text-gray-700">Note</label>
                  <textarea v-model="form.Notes" rows="4" class="p93__input w-full"></textarea>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Accordion Section 2: Righe Contratto -->
        <div class="mb-10 bg-white rounded-md shadow-lg" v-if="form.Customer">
          <div 
            @click="toggleSection('contractLines')" 
            class="flex justify-between items-center px-6 py-3 cursor-pointer bg-gray-100 rounded-t-md"
          >
            <h3 class="font-semibold">Righe Contratto</h3>
            <span class="transform transition-transform" :class="{'rotate-180': !sections.contractLines}">
              &#9660;<!-- Black down arrow Unicode character -->
            </span>
          </div>
          <div v-show="sections.contractLines" class="pt-6 px-6 pb-2">
            <div v-if="errors.general2" class="mb-3 text-red-800">{{ errors.general2 }}</div>
            <div class="text-gray-700">
              <!-- Tabella per visualizzare le righe del contratto -->
              <div class="mb-4">
                <div class="inline-flex">
                  <button type="button" @click="openLineModal()" class="bg-blue-500 text-white px-4 py-2 rounded-md mb-4">
                    <i class="fa fa-plus mr-1"></i> Aggiungi riga
                  </button>
                  <label class="flex items-center cursor-pointer ml-4">
                    <input type="checkbox" v-model="showDisabled" class="sr-only" />
                    <div class="relative w-10 h-5 bg-gray-300 rounded-full transition-colors duration-200"
                      :class="{ 'bg-green-500': showDisabled }">
                      <span
                        class="absolute left-0 top-0 h-5 w-5 bg-white rounded-full shadow transform transition-transform duration-200"
                        :class="{ 'translate-x-5': showDisabled }" />
                    </div>
                    <span class="ml-3 text-gray-700">Mostra disattivi</span>
                  </label>
                </div>
                
                <div v-if="contractLines.length === 0" class="text-center py-8 bg-gray-50 border border-gray-200 rounded-md">
                  <p class="text-gray-500">Nessuna riga contratto presente</p>
                  <button type="button" @click="openLineModal()" class="mt-2 text-blue-500 underline">Aggiungi la prima riga</button>
                </div>
                
                <div v-else class="overflow-x-auto contract-lines-section">
                  <table class="min-w-full bg-white border border-gray-200">
                    <thead>
                      <tr class="bg-gray-100">
                        <th class="py-2 px-4 border-b text-left">Codice</th>
                        <th class="py-2 px-4 border-b text-left">Descrizione</th>
                        <th class="py-2 px-4 border-b text-center">Data inizio</th>
                        <th class="py-2 px-4 border-b text-center">Data fine</th>
                        <th class="py-2 px-4 border-b text-center">UM</th>
                        <th class="py-2 px-4 border-b text-center">Qtà</th>
                        <th class="py-2 px-4 border-b text-center">Valore unitario</th>
                        <th class="py-2 px-4 border-b text-center">Totale</th>
                        <th class="py-2 px-4 border-b text-center">Attivo</th>
                        <th class="py-2 px-4 border-b text-center">Azioni</th>
                      </tr>
                    </thead>
                    <tbody>
                      <template v-for="(line, index) in contractLines" :key="index">
                        <tr class="hover:bg-gray-50" v-if="line.Disabled == 0 || showDisabled">
                          <td class="py-2 px-4 border-b">{{ line.Item }}</td>
                          <td class="py-2 px-4 border-b">{{ line.Description }}</td>
                          <td class="py-2 px-4 border-b text-center">{{ (line.StartDate) ? formatDate(line.StartDate) : '-' }}</td>
                          <td class="py-2 px-4 border-b text-center">{{ (line.EndDate) ? formatDate(line.EndDate) : '-' }}</td>
                          <td class="py-2 px-4 border-b text-center">{{ line.UoM }}</td>
                          <td class="py-2 px-4 border-b text-center">{{ formatNumber(line.Qty) }}</td>
                          <td class="py-2 px-4 border-b text-right">{{ formatCurrency(line.UnitValue, 3) }}</td>
                          <td class="py-2 px-4 border-b text-right">{{ formatCurrency(line.TotalAmount, 3) }}</td>
                          <td class="py-2 px-4 border-b text-center">
                            <span v-if="line.Disabled == '1'" class="text-red-600">X</span>
                            <span v-else class="text-green-600">✓</span>
                          </td>
                          <td class="py-2 px-4 border-b text-center">
                            <div class="flex justify-center space-x-2">
                              <button v-if="line.RowId" type="button" @click="showBillingDetails(line)" class="text-green-500 hover:text-green-700">
                                <i class="fa fa-euro-sign"></i>
                              </button>
                              <button type="button" @click="editLine(index)" class="text-blue-500 hover:text-blue-700">
                                <i class="fa fa-edit"></i>
                              </button>
                              <button type="button" @click="rowDeletion=line; confirmDeleteShow = true;" class="text-red-500 hover:text-red-700">
                                <i class="fa fa-trash"></i>
                              </button>
                            </div>
                          </td>
                        </tr>

                        <tr v-if="BillingDetailRef == line && (line.Disabled == 0 || showDisabled)">
                          <td colspan="11" class="p-2">
                            <div class="mb-4">
                              <div class="overflow-x-auto">
                                <table class="min-w-full bg-white border border-gray-200">
                                  <thead>
                                    <tr class="bg-gray-100">
                                      <th class="py-2 px-4 border-b text-left">Rata nr.</th>
                                      <th class="py-2 px-4 border-b text-center">Ordine nr.</th>
                                      <th class="py-2 px-4 border-b text-center">Fatturato</th>
                                      <th class="py-2 px-4 border-b text-right">Importo</th>
                                      <th class="py-2 px-4 border-b text-center">Validità rata</th>
                                      <th class="py-2 px-4 border-b text-center">Azioni</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    <tr v-for="(line, index) in currentBillingDetails" :key="index" class="hover:bg-gray-50">
                                      <td class="py-2 px-4 border-b">{{ line.InstallmentNo }}</td>
                                      <td class="py-2 px-4 border-b text-center">{{ line.InternalOrdNo ?? '-' }}</td>
                                      <td class="py-2 px-4 border-b text-center">
                                        <span v-if="line.Invoiced === '1'" class="text-green-600">✓</span>
                                        <span v-else class="text-red-600">✕</span>
                                      </td>
                                      <td class="py-2 px-4 border-b text-right">{{ formatCurrency(line.Amount, 3) }}</td>
                                      <td class="py-2 px-4 border-b text-center">{{ formatDate(line.DueDate) }}</td>
                                      <td class="py-2 px-4 border-b text-center">
                                        <button type="button" @click="editBillingDetail(index)" class="text-blue-500 hover:text-blue-700">
                                          <i class="fa fa-edit"></i>
                                        </button>
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </td>
                        </tr>
                      </template>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Dettaglio billing -->
        <!--<div class="mb-4 bg-white rounded-md shadow-lg billing-details-section" v-if="showBillingDetailsSection">
          <div 
            @click="toggleSection('billingDetails')" 
            class="flex justify-between items-center px-6 py-3 cursor-pointer bg-gray-100 rounded-t-md"
          >
            <h3 class="font-semibold">Dettagli Ordini</h3>
            <span class="transform transition-transform" :class="{'rotate-180': !sections.billingDetails}">
              &#9660;<!-- Black down arrow Unicode character -->
            <!--</span>
          </div>
          <div v-show="sections.billingDetails" class="p-6">
            <div class="text-gray-700">
              <!-- Tabella per visualizzare le righe del contratto -->
              <!--<div class="mb-4">
                <div class="overflow-x-auto">
                  <table class="min-w-full bg-white border border-gray-200">
                    <thead>
                      <tr class="bg-gray-100">
                        <th class="py-2 px-4 border-b text-left">Rata nr.</th>
                        <th class="py-2 px-4 border-b text-center">Ordine nr.</th>
                        <th class="py-2 px-4 border-b text-center">Fatturato</th>
                        <th class="py-2 px-4 border-b text-right">Importo</th>
                        <th class="py-2 px-4 border-b text-center">Scadenza</th>
                        <th class="py-2 px-4 border-b text-center">Azioni</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(line, index) in currentBillingDetails" :key="index" class="hover:bg-gray-50">
                        <td class="py-2 px-4 border-b">{{ line.InstallmentNo }}</td>
                        <td class="py-2 px-4 border-b text-center">{{ line.InternalOrdNo ?? '-' }}</td>
                        <td class="py-2 px-4 border-b text-center">
                          <span v-if="line.Invoiced === '1'" class="text-green-600">✓</span>
                          <span v-else class="text-red-600">✕</span>
                        </td>
                        <td class="py-2 px-4 border-b text-right">{{ formatCurrency(line.Amount, 3) }}</td>
                        <td class="py-2 px-4 border-b text-center">{{ formatDate(line.DueDate) }}</td>
                        <td class="py-2 px-4 border-b text-center">
                          <button type="button" @click="editBillingDetail(index)" class="text-blue-500 hover:text-blue-700">
                            <i class="fa fa-edit"></i>
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>-->
      </div>
    </form>
    
    <!-- Modal per aggiungere/modificare una riga -->
    <div v-if="showLineModal" class="fixed inset-0 flex items-center justify-center z-50">
      <div class="absolute inset-0 bg-black opacity-50" @click="closeLineModal"></div>
      <div class="bg-white rounded-lg shadow-xl p-6 w-11/12 z-10 max-h-[80vh] overflow-y-auto modal-content">
        <h3 class="text-lg font-semibold mb-4">{{ editingLineIndex === null ? 'Aggiungi' : 'Modifica' }} riga contratto</h3>
        <form @submit.prevent="saveLine" @keydown.enter.prevent.stop>

          <div class="grid grid-cols-1 md:grid-cols-3 gap-4">

            <div class="mb-3">
              <label class="block text-gray-700 mb-1">Codice</label>
              <v-select :options="foundItems" :filterable="false" v-model="selectedItem" label="displayName"
                @search="searchItems" @option:selected="onItemSelect" required>
                <template #no-options="{ search, searching }">
                  <div v-if="searching">🔍 Ricerca in corso...</div>
                  <div v-else-if="!search">Inizia a digitare per cercare un articolo...</div>
                </template>
              </v-select>
              <div v-if="errors.Item" class="py-4 text-red-800">{{ errors.Item }}</div>
            </div>

            <div class="mb-3">
              <label class="block text-gray-700 mb-1">Commessa</label>
              <select v-model="lineForm.Job" class="p93__input" :disabled="!form.Customer">
                <option value="">Scegli la commessa</option>
                <option v-for="job in jobs" :key="job.id" :value="job.id">{{ job.name }}</option>
              </select>
            </div>

            <div class="mb-3">
              <label class="block text-gray-700 mb-1">Data attivazione</label>
              <VueDatePicker v-model="lineForm.ActivationDate" locale="it" format="dd/MM/yyyy" auto-apply
                            :enable-time-picker="false" text-input
                            class="w-full border border-neutral-300 rounded-md shadow-md" @input="pageDateInput"/>
              <div v-if="errors.ActivationDate" class="py-4 text-red-800">{{ errors.ActivationDate }}</div>
            </div>

          </div>

          <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          
            <div class="mb-3">
              <label class="block text-gray-700 mb-1">Data inizio</label>
              <VueDatePicker v-model="lineForm.StartDate" locale="it" format="dd/MM/yyyy" auto-apply
                            :enable-time-picker="false" text-input
                            class="w-full border border-neutral-300 rounded-md shadow-md" @input="pageDateInput"/>
              <div v-if="errors.StartDate" class="py-4 text-red-800">{{ errors.StartDate }}</div>
            </div>
            
            <div class="mb-3">
              <label class="block text-gray-700 mb-1">Data fine</label>
              <VueDatePicker v-model="lineForm.EndDate" locale="it" format="dd/MM/yyyy" auto-apply
                            :enable-time-picker="false" text-input :required="form.ContractType != 'PCO'"
                            class="w-full border border-neutral-300 rounded-md shadow-md" @input="pageDateInput"/>
              <div v-if="errors.EndDate" class="py-4 text-red-800">{{ errors.EndDate }}</div>
            </div>
          </div>
          <div class="grid grid-cols-1 gap-4">
            
            <div class="mb-3">
              <label class="block text-gray-700 mb-1">Descrizione</label>
              <input v-model="lineForm.Description" type="text" class="w-full p-2 border border-gray-300 rounded-md">
              <div v-if="errors.Description" class="py-4 text-red-800">{{ errors.Description }}</div>
            </div>

          </div>

          <div class="grid grid-cols-1 md:grid-cols-4 gap-4">

            <div class="mb-3">
              <label class="block text-gray-700 mb-1">Unità di misura</label>
              <select v-model="lineForm.UoM" class="w-full p-2 border border-gray-300 rounded-md">
                <option v-for="unitOfMeasure in unitsOfMeasure" :key="unitOfMeasure.id" :value="unitOfMeasure.id">{{ unitOfMeasure.name }}</option>
              </select>
              <div v-if="errors.UoM" class="py-4 text-red-800">{{ errors.UoM }}</div>
            </div>
            
            <div class="mb-3">
              <label class="block text-gray-700 mb-1">Quantità</label>
              <input v-model.number="lineForm.Qty" type="number" min="0" step="1" class="w-full p-2 border border-gray-300 rounded-md" @keypress="onlyNumbers">
              <div v-if="errors.Qty" class="py-4 text-red-800">{{ errors.Qty }}</div>
            </div>
            
            <div class="mb-3">
              <label class="block text-gray-700 mb-1">Valore unitario</label>
              <input v-model="lineForm.UnitValue" type="text" class="w-full p-2 border border-gray-300 rounded-md">
              <div v-if="errors.UnitValue" class="py-4 text-red-800">{{ errors.UnitValue }}</div>
            </div>

            <div class="mb-3">
              <label class="block text-gray-700 mb-1">Copie incluse</label>
              <input v-model.number="lineForm.CopyCost" type="text" class="w-full p-2 border border-gray-300 rounded-md">
              <div v-if="errors.CopyCost" class="py-4 text-red-800">{{ errors.CopyCost }}</div>
            </div>
            
            <div class="mb-3">
              <label class="block text-gray-700 mb-1">Sconto Formula</label>
              <input v-model="lineForm.DiscountFormula" class="w-full p-2 border border-gray-300 rounded-md" @keypress="onlyNumbersAndPlusMinus">
              <div v-if="errors.DiscountFormula" class="py-4 text-red-800">{{ errors.DiscountFormula }}</div>
            </div>

            <div class="mb-3">
              <label class="block text-gray-700 mb-1">Adeguamento prossimo rinnovo</label>
              <input v-model.number="lineForm.RenewalAdjustment" class="w-full p-2 border border-gray-300 rounded-md" @keypress="onlyNumbers">
              <div v-if="errors.RenewalAdjustment" class="py-4 text-red-800">{{ errors.RenewalAdjustment }}</div>
            </div>
            
            <div class="mb-3">
              <label class="block text-gray-700 mb-1">Totale</label>
              <input v-model.number="lineForm.TotalAmount" class="w-full p-2 border border-gray-300 rounded-md bg-gray-100" readonly>
              <div v-if="errors.TotalAmount" class="py-4 text-red-800">{{ errors.TotalAmount }}</div>
            </div>

            <div class="mb-3">
                  <label for="contractLineDisabled" class="flex items-center cursor-pointer">
                    <input id="contractLineDisabled" type="checkbox" v-model="lineForm.Disabled" class="sr-only" />
                    <div class="relative w-10 h-5 bg-gray-300 rounded-full transition-colors duration-200"
                      :class="{ 'bg-red-500': lineForm.Disabled }">
                      <span
                        class="absolute left-0 top-0 h-5 w-5 bg-white rounded-full shadow transform transition-transform duration-200"
                        :class="{ 'translate-x-5': lineForm.Disabled }" />
                    </div>
                    <span class="ml-3 text-gray-700">Disabilitato</span>
                  </label>
                  <div v-if="errors.Disabled" class="py-4 text-red-800">{{ errors.Disabled }}</div>
                  <div class="mt-3" v-if="!lineForm.RowId && form.ContractType != 'PCO'">
                    <label for="contractLineInvoiced" class="flex items-center cursor-pointer">
                      <input id="contractLineInvoiced" type="checkbox" v-model="lineForm.AlreadyInvoiced" class="sr-only" />
                      <div class="relative w-10 h-5 bg-gray-300 rounded-full transition-colors duration-200"
                        :class="{ 'bg-green-500': lineForm.AlreadyInvoiced }">
                        <span
                          class="absolute left-0 top-0 h-5 w-5 bg-white rounded-full shadow transform transition-transform duration-200"
                          :class="{ 'translate-x-5': lineForm.AlreadyInvoiced }" />
                      </div>
                      <span class="ml-3 text-gray-700"> Rata già Fatturata</span>
                    </label>
                    <div v-if="lineForm.AlreadyInvoiced === '1' || lineForm.AlreadyInvoiced === true" class="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <div class="mb-3">
                        <label class="block text-gray-700 mb-1">Numero Fattura Mago</label>
                        <input v-model="lineForm.MagoInvoice" type="text" class="w-full p-2 border border-gray-300 rounded-md">
                        <div v-if="errors.MagoInvoice" class="py-4 text-red-800">{{ errors.MagoInvoice }}</div>
                      </div>
                      <div class="mb-3">
                        <label class="block text-gray-700 mb-1">Linea della fattura</label>
                        <input v-model="lineForm.MagoInvoiceLine" type="number" class="w-full p-2 border border-gray-300 rounded-md">
                        <div v-if="errors.MagoInvoiceLine" class="py-4 text-red-800">{{ errors.MagoInvoiceLine }}</div>
                      </div>
                    </div>
                    <div v-if="errors.AlreadyInvoiced" class="py-4 text-red-800">{{ errors.AlreadyInvoiced }}</div>
                  </div>
                </div>
          </div>
          
          <div class="flex justify-end mt-4 space-x-2">
            <button @click="closeLineModal" class="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400">
              Annulla
            </button>
            <button class="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600">
              Salva
            </button>
          </div>
        </form>
      </div>
    </div>
    
    <!-- Modal per modificare dettaglio fatturazione -->
    <div v-if="showBillingDetailModal" class="fixed inset-0 flex items-center justify-center z-50">
      <div class="absolute inset-0 bg-black opacity-50" @click="closeBillingDetailModal"></div>
      <div class="bg-white rounded-lg shadow-xl p-6 w-11/12 max-w-2xl z-10 max-h-[80vh] overflow-y-auto modal-content">
        <h3 class="text-lg font-semibold mb-4">Modifica Dettaglio Ordine</h3>
        <form @submit.prevent="saveBillingDetail" @keydown.enter.prevent.stop>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">

            <div class="mb-3">
              <label class="block text-gray-700 mb-1">Rata nr.</label>
              <input v-model="billingDetailForm.InstallmentNo" type="number" class="w-full p-2 border border-gray-300 rounded-md" readonly>
            </div>

            <div class="mb-3">
              <label class="block text-gray-700 mb-1">Ordine nr.</label>
              <input v-model="billingDetailForm.InternalOrdNo" type="text" class="w-full p-2 border border-gray-300 rounded-md">
            </div>

            <div class="mb-3">
              <label class="block text-gray-700 mb-1">Importo</label>
              <input v-model="billingDetailForm.Amount" type="number" step="0.00001" class="w-full p-2 border border-gray-300 rounded-md">
            </div>

            <div class="mb-3">
              <label class="block text-gray-700 mb-1">Scadenza</label>
              <VueDatePicker v-model="billingDetailForm.DueDate" locale="it" format="dd/MM/yyyy" auto-apply
                            :enable-time-picker="false" text-input :teleport="true"
                            class="w-full border border-neutral-300 rounded-md shadow-md" @input="pageDateInput"/>
            </div>

            <div class="mb-3 flex items-center">
              <label class="flex items-center">
                <input 
                  v-model="billingDetailForm.Invoiced" 
                  type="checkbox" 
                  class="mr-2"
                >
                <span>Fatturato</span>
              </label>
            </div>

          </div>
          
          <div class="flex justify-end mt-4 space-x-2">
            <button type="button" @click="closeBillingDetailModal" class="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400">
              Annulla
            </button>
            <button type="submit" class="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600">
              Salva
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
  <div class="fixed left-0 right-0 bottom-0 flex justify-center items-center bg-white pb-2 pt-2 z-2">
      <button type="button" @click="resetForm" class="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md mr-2">
      <i class="fa fa-undo"></i> Reset</button>
      <button class="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-md" @click="submitForm" v-if="form.Customer">
      <i class="fa fa-save"></i> Salva Modifiche
      </button>
  </div>
</template>

<script>
import { Link, router, useForm } from '@inertiajs/vue3';
import BreadCrumbs from '@/Components/BreadCrumbs.vue';
import axios from 'axios';
import Datepicker from 'vue3-datepicker';
import { it } from 'date-fns/locale';
import { parseISO } from 'date-fns';

export default {
  props: ['lastContractCode', 'errors', 'agents', 'areaManagers', 'priceLists', 'paymentTerms', 'contractModels', 'unitsOfMeasure', 'billingPeriods', 'contract', 'isEditing', 'pageURL', 'pageURLName', 'prefillData'],
  components: {
    Link,
    BreadCrumbs,
    Datepicker
  },
  data() {
    return {
      foundClients: [],
      foundInvoiceClients: [],
      sites: [],
      jobs: [],
      localContractLines: [],
      localContractLinesInitialized: false,
      contractLinesbck: [],
      showLineModal: false,
      showItemSearchModal: false,
      editingLineIndex: null,
      isSearchingItems: false,
      itemSearchQuery: '',
      foundItems: [],
      selectedItem: null,
      itemsPagination: {
        currentPage: 1,
        lastPage: 1,
        perPage: 10,
        total: 0,
        links: []
      },
      billingDetails: [],
      showBillingDetailsSection: false,
      localBillingDetails: [],
      localBillingDetailsInitialized: false,
      billingDetailsbck: [],
      showBillingDetailModal: false,
      BillingDetailRef: null,
      editingBillingDetailIndex: null,
      billingDetailForm: {
        InstallmentNo: '',
        InternalOrdNo: '',
        Amount: 0,
        DueDate: null,
        Invoiced: false
      },
      lineForm: useForm({
        ContractId: this.contract?.ContractId || null,
        Item: '',
        Description: '',
        StartDate: null,
        EndDate: null,
        ActivationDate: null,
        UoM: 'NR',
        Qty: 1,
        UnitValue: 0,
        CopyCost: 0,
        DiscountFormula: 0,
        RenewalAdjustment: 0,
        TotalAmount: 0,
        Disabled: false,
        BillingPeriod: 0,
        ActivationDate: null,
        Job: '',
        AlreadyInvoiced: false,
        MagoInvoice: '',
        MagoInvoiceLine: null
      }),
      form: useForm({
        ContractId: this.contract?.ContractId || null,
        DocNo: this.contract?.DocNo ?? this.calculateNextCode(this.lastContractCode),
        Model: this.contract?.Model,
        Customer: this.contract?.Customer ? {'id': this.contract.Customer, 'name': this.contract.CompanyName} : null,
        ContractPlace: this.contract?.ContractPlace,
        ContractType: this.contract?.ContractType ?? 'SER',
        BillingPeriod: this.contract?.BillingPeriod ?? 'ANN',
        Job: this.contract?.Job ?? '',
        PriceList: this.contract?.PriceList,
        PaymentCondition: this.contract?.PaymentCondition,
        SalePerson: this.contract?.SalePerson,
        AreaManager: this.contract?.AreaManager,
        UtilizerPlace: this.contract?.UtilizerPlace,
        StipulationDate: this.contract?.StipulationDate ? parseISO(this.contract.StipulationDate) : null,
        LastRevision: this.contract?.LastRevision ? parseISO(this.contract.LastRevision) : null,
        Disabled: this.contract?.Disabled ? Boolean(parseInt(this.contract.Disabled)) : false,
        SignedContract: this.contract?.SignedContract ? Boolean(parseInt(this.contract.SignedContract)) : false,
        Notes: this.contract?.Notes ?? '',
        contractLines: this.contract?.contractLines ?? [], // Add this to store contract lines
        billingDetails: [], // Add this to store billing details modifications
        sourceOrderId: null,
        sourceDetailIds: []
      }),
      currentLastContractCode: this.calculateNextCode(this.lastContractCode),
      sections: {
        contractDetails: false,
        contractLines: true,
        billingDetails: false,
      },
      dateFormat: 'dd/MM/yyyy',
      dateLocale: it,
      confirmDeleteShow: false,
      rowDeletion: null,
      showDisabled: false,
      contractTypes: [
          { value: 'SER', name: 'Servizi' },
          { value: 'NOL', name: 'Noleggio' },
          { value: 'PCO', name: 'Pacchetto Ore' }
      ],
    };
  },
  computed: {
    navigation(){
      return [
        { name: 'Dashboard', href: route('dashboard') },
        { name: 'Contratti', href: route('modules.home', 'contratti') },
        { name: this.pageURLName ?? 'Nuovo contratto', href: this.pageURL ?? route('modules.contratti.nuovo') }
      ];
    },
    pageTitle() {
      if (this.isEditing) {
        const contractCode = this.form.DocNo ? ` - ${this.form.DocNo}` : '';
        const clientName = this.form.Customer?.name ? ` - ${this.form.Customer.name}` : '';
        return `Modifica Contratto${contractCode}${clientName}`;
      }
      return 'Nuovo Contratto';
    },
    contractLines() {
      // Se ci sono modifiche locali inizializzate, usa quelle, altrimenti usa le righe dal contratto
      if (this.localContractLinesInitialized) {
        return this.localContractLines;
      }
      return this.contract?.contractLines ?? [];
    },
    currentBillingDetails() {
      // Se ci sono modifiche locali inizializzate, usa quelle, altrimenti usa i dati originali
      if (this.localBillingDetailsInitialized) {
        return this.localBillingDetails;
      }
      return this.billingDetails;
    }
  },
  methods: {
    toggleSection(section) {
      this.sections[section] = !this.sections[section];
    },
    calculateNextCode(lastCode) {
      const currentYear = new Date().getFullYear().toString().slice(-2);
      if (!lastCode) return `${currentYear}/00001`;
      const parts = lastCode.split('/');
      const year = parts[0];
      if (year !== currentYear) {
        return `${currentYear}/00001`;
      }
      const number = parseInt(parts[1], 10);
      return `${year}/${(number + 1).toString().padStart(5, '0')}`;
    },
    calculateDiscountedAmount(originalAmount, discountFormula) {
      if (!discountFormula || discountFormula === '') {
        return originalAmount;
      }

      let formula = String(discountFormula).trim();
      formula = formula.replace(/-/g, '+-');
      
      if (formula.includes('+')) {
        const discountParts = formula.split('+');
        let remainingAmount = originalAmount;
        
        for (const part of discountParts) {
          const discount = parseFloat(part.trim());
          if (discount > 0 && discount <= 100) {
            remainingAmount = remainingAmount * (1 - discount / 100);
          }
        }
        
        return remainingAmount;
      } else {
        const discount = parseFloat(formula);
        if (discount > 0 && discount <= 100) {
          return originalAmount * (1 - discount / 100);
        }
      }
      
      return originalAmount;
    },
    submitForm() {
      // Clone the contract lines to avoid reactive issues
      this.form.contractLines = JSON.parse(JSON.stringify(this.contractLines));
      this.contractLinesbck = JSON.parse(JSON.stringify(this.contractLines));

      if (this.prefillData) {
        this.form.sourceDetailIds = this.contractLines
          .filter(line => line._sourceDetailId)
          .map(line => line._sourceDetailId);
      }
      
      // Include billing details modifications if any
      console.log('Local Billing Details Initialized:', this.localBillingDetailsInitialized);
      if (this.localBillingDetailsInitialized) {
        this.form.billingDetails = JSON.parse(JSON.stringify(this.localBillingDetails));
      }
      this.billingDetailsbck = JSON.parse(JSON.stringify(this.currentBillingDetails));
      
      // Submit the form with the complete data
      if (this.isEditing) {
        this.updateContract();
      } else {
        this.addContract();
      }
    },
    addContract() {
      this.form.post(route('modules.contratti.aggiungi'), {
        preserveScroll: true,
        preserveState: true,
        only: ['errors', 'contract', 'isEditing', 'pageURL', 'pageURLName'],
        onSuccess: () => {
          if (this.prefillData && this.form.sourceOrderId) {
            try {
              localStorage.setItem('p93_contract_created', JSON.stringify({
                sourceOrderId: this.form.sourceOrderId,
                ts: Date.now()
              }));
            } catch (e) {
              // ignore storage errors
            }
            window.close();
          }
        },
        onError: (error) => {
          console.log(error);
          this.$nextTick(() => {
            this.localContractLinesInitialized = true;
            this.localContractLines = this.contractLinesbck;
            this.localBillingDetailsInitialized = true;
            this.localBillingDetails = this.billingDetailsbck;
          });
        }
      });
    },
    updateContract() {
      this.form.ContractId = this.contract.ContractId;
      this.form.post(route('modules.contratti.modifica'), {
        preserveScroll: true,
        preserveState: true,
        only: ['errors', 'contract', 'isEditing'],
        onError: (error) => {
          this.$nextTick(() => {
            if(error.general2)
            {
              this.sections.contractLines = true;
              const contractlinessection = document.querySelector('.contract-lines-section');
              if (contractlinessection) {
                contractlinessection.scrollIntoView({ behavior: 'smooth' });
              }
            }
            else
            {
              this.sections.contractDetails = true;
              const contractdetailssection = document.querySelector('.contract-details-section');
              if (contractdetailssection) {
                contractdetailssection.scrollIntoView({ behavior: 'smooth' });
              }
            }
            this.localContractLinesInitialized = true;
            this.localContractLines = this.contractLinesbck;
            this.localBillingDetailsInitialized = true;
            this.localBillingDetails = this.billingDetailsbck;
          });
        }
      });
    },
    searchClients(search, loading) {
      if (search.length >= 2) {
        loading(true);
        clearTimeout(this.searchContactsTimeout);
        this.searchContactsTimeout = setTimeout(() => {
          axios
            .get(route('modules.contratti.ricerca-clienti', search))
            .then((response) => {
              console.log(response.data);
              this.foundClients = response.data.map(client => ({
                ...client,
                name: `${client.id} - ${client.name}`
              }));
              loading(false);
            });
        }, 300);
      }
    },
    searchInvoiceClients(search, loading) {
      if (search.length >= 2) {
        loading(true);
        clearTimeout(this.searchInvoiceContactsTimeout);
        this.searchInvoiceContactsTimeout = setTimeout(() => {
          axios
            .get(route('modules.contratti.ricerca-clienti', search))
            .then((response) => {
              this.foundInvoiceClients = response.data;
              loading(false);
            });
        }, 300);
      }
    },
    onClientSelect(selectedClient, setDefault = true) {
      this.getJobs(selectedClient);
      this.getSites(selectedClient);
      if (setDefault) {
        this.setDefaultPaymentTerm(selectedClient);
        this.setDefaultPriceList(selectedClient);
        this.setDefaultAreaManagerAndSalesPerson(selectedClient);
      }
    },
    getSites(clientId) {
      axios.get(route('modules.contratti.sedi', clientId))
        .then((response) => {
          this.sites = response.data.map(item => {
            const name = `${item.Address} ${item.ZIPCode} ${item.City} ${item.County} - ${item.CompanyName}`;
            return {
              id: item.id,
              name
            };
          });
        }
      );
    },
    setDefaultPaymentTerm(selectedClient) {
      this.form.PaymentCondition = this.foundClients.find(client => client.id === selectedClient.id)?.Payment;
    },
    setDefaultPriceList(selectedClient) {
      this.form.PriceList = this.foundClients.find(client => client.id === selectedClient.id)?.PriceList;
    },
    setDefaultAreaManagerAndSalesPerson(selectedClient) {
      axios.get(route('modules.contratti.opzioniCliente', selectedClient.id))
        .then((response) => {
          this.form.AreaManager = response.data.AreaManager;
          this.form.SalePerson = response.data.Salesperson;
        });
    },
    getJobs(clientId) {
      axios.get(route('modules.contratti.commesse', clientId))
        .then((response) => {
          this.jobs = response.data.map(item => {
            const name = `${item.id} - ${item.Description}`;
            return {
              id: item.id,
              name
            };
          });
        });
    },
    openLineModal() {
      this.resetLineForm();
      this.showLineModal = true;
      this.editingLineIndex = null;
    },
    closeLineModal() {
      this.showLineModal = false;
    },
    resetLineForm() {
      this.lineForm.reset();

      var baselineRow = {
        ContractId: this.contract?.ContractId || null,
        Item: '',
        Description: '',
        StartDate: null,
        EndDate: null,
        ActivationDate: null,
        UoM: 'NR',
        Qty: 1,
        UnitValue: 0,
        CopyCost: 0,
        DiscountFormula: 0,
        RenewalAdjustment: 0,
        TotalAmount: 0,
        Disabled: false,
        BillingPeriod: 0,
        ActivationDate: null,
        Job: this.form.Job,
        AlreadyInvoiced: false,
        MagoInvoice: '',
        MagoInvoiceLine: null
      };

      const internalKeys = Object.keys(useForm({}));

      //Elimina tutti i campi non presenti in baselineRow che non sono interni a useForm (es. RowId dopo modifica)
      Object.keys(this.lineForm).forEach(key => {
        if (!(key in baselineRow) && !internalKeys.includes(key)) {
          delete this.lineForm[key];
        }
      });

      Object.keys(baselineRow).forEach(key => {
        this.lineForm[key] = baselineRow[key];
      });

      /*this.lineForm.Item = '';
      this.lineForm.Description = '';
      this.lineForm.StartDate = null;
      this.lineForm.EndDate = null;
      this.lineForm.ActivationDate = null;
      this.lineForm.UoM = 'NR';
      this.lineForm.Qty = 1;
      this.lineForm.UnitValue = 0;
      this.lineForm.CopyCost = 0;
      this.lineForm.DiscountFormula = 0;
      this.lineForm.RenewalAdjustment = 0;
      this.lineForm.TotalAmount = 0;
      this.lineForm.Disabled = false;
      this.lineForm.BillingPeriod = 0;
      this.lineForm.Job = '';*/
      this.selectedItem = null;
      this.lineForm.clearErrors();
    },
    saveLine() {
      console.log(this.lineForm.data())
      this.lineForm.post(route('modules.contratti.controllaRiga'), {
        onSuccess: (response) => {
          // Assicurati che le modifiche locali siano inizializzate
          if (!this.localContractLinesInitialized) {
            this.localContractLines = [...(this.contract?.contractLines ?? [])];
            this.localContractLinesInitialized = true;
          }

          if (this.lineForm.ActivationDate)
          {
            this.lineForm.ActivationDate = new Date(this.lineForm.ActivationDate - this.lineForm.ActivationDate.getTimezoneOffset() * 60000).toISOString().split('T')[0];
          }

          if (this.lineForm.StartDate)
          {
            this.lineForm.StartDate = new Date(this.lineForm.StartDate - this.lineForm.StartDate.getTimezoneOffset() * 60000).toISOString().split('T')[0];
          }

          if (this.lineForm.EndDate)
          {
            this.lineForm.EndDate = new Date(this.lineForm.EndDate - this.lineForm.EndDate.getTimezoneOffset() * 60000).toISOString().split('T')[0];
          }
          
          const savedLine = JSON.parse(JSON.stringify(this.lineForm));
          if (this.lineForm._sourceDetailId) {
            savedLine._sourceDetailId = this.lineForm._sourceDetailId;
          }

          if (this.editingLineIndex === null) {
            this.localContractLines.push(savedLine);
          } else {
            this.localContractLines.splice(this.editingLineIndex, 1, savedLine);
          }
          this.closeLineModal();
        },
        preserveScroll: true,
        preserveState: true,
        only: ['errors']
      });
    },
    editLine(index) {
      this.editingLineIndex = index;
      const line = this.contractLines[index];
      
      this.resetLineForm();

      this.lineForm.Item = line.Item;
      this.lineForm.RowId = line.RowId || null; // Mantieni RowId se esiste
      this.lineForm._sourceDetailId = line._sourceDetailId || null;
      this.lineForm.Description = line.Description;
      this.lineForm.StartDate = line.StartDate ? parseISO(line.StartDate) : null;
      this.lineForm.EndDate = line.EndDate ? parseISO(line.EndDate) : null;
      this.lineForm.ActivationDate = line.ActivationDate ? parseISO(line.ActivationDate) : null;
      this.lineForm.UoM = line.UoM;
      this.lineForm.Qty = line.Qty;
      this.lineForm.UnitValue = line.UnitValue;
      this.lineForm.CopyCost = line.CopyCost;
      this.lineForm.DiscountFormula = line.DiscountFormula;
      this.lineForm.RenewalAdjustment = line.RenewalAdjustment;
      this.lineForm.TotalAmount = this.formatNumber(line.TotalAmount);
      this.lineForm.Disabled = line.Disabled == 1;
      this.lineForm.Job = line.Job;
      this.lineForm.AlreadyInvoiced = line.AlreadyInvoiced ?? false,
      this.lineForm.MagoInvoice = line.MagoInvoice ?? '',
      this.lineForm.MagoInvoiceLine = line.MagoInvoiceLine ?? null,
      // Imposta selectedItem per la v-select con tutti i campi necessari
      this.selectedItem = {
        id: line.Item,
        code: line.Item,
        description: line.Description,
        unitOfMeasure: line.UoM,
        price: line.UnitValue,
        displayName: `${line.Item} - ${line.Description}`
      };
      // Aggiungi l'articolo alla lista foundItems se non è già presente
      if (!this.foundItems.find(item => item.id === line.Item)) {
        this.foundItems.push(this.selectedItem);
      }
      this.showLineModal = true;
    },
    deleteLine(index) {
      // Assicurati che le modifiche locali siano inizializzate
      if (!this.localContractLinesInitialized) {
        this.localContractLines = [...(this.contract?.contractLines ?? [])];
        this.localContractLinesInitialized = true;
      }
      this.localContractLines.splice(index, 1);
    },
    searchItems(search, loading) {
      if (search.length >= 2) {
        loading(true);
        clearTimeout(this.searchItemsTimeout);
        this.searchItemsTimeout = setTimeout(() => {
          axios
            .get(route('modules.contratti.ricerca-articoli', search))
            .then((response) => {
              console.log(response);
              // Assegna gli elementi trovati
              this.foundItems = response.data.map(item => ({
                id: item.Item,
                code: item.Item,
                description: item.Description,
                unitOfMeasure: item.BaseUoM,
                price: item.BasePrice || 0,
                displayName: `${item.Item} - ${item.Description}`
              }));
              
              loading(false);
            })
            .catch(error => {
              console.error('Errore durante la ricerca degli articoli:', error);
              loading(false);
            });
        }, 300);
      }
    },
    onItemSelect(selectedItem) {
      if (selectedItem) {
        this.lineForm.Item = selectedItem.code;
        this.lineForm.Description = selectedItem.description;
        this.lineForm.UoM = selectedItem.unitOfMeasure;
        this.lineForm.UnitValue = parseFloat(selectedItem.price).toFixed(3);
      }
    },
    deleteItem(index) {
      if (confirm('Sei sicuro di voler eliminare questo articolo?')) {
        this.foundItems.splice(index, 1);
      }
    },
    onlyNumbers(event) {
      const keyCode = event.keyCode || event.which;
      const keyValue = String.fromCharCode(keyCode);
      if (!/^\d+$/.test(keyValue) && keyCode !== 8 && keyCode !== 46 && keyCode !== 37 && keyCode !== 39) {
        event.preventDefault();
      }
    },
    onlyNumbersAndPlusMinus(event) {
      const keyCode = event.keyCode || event.which;
      const keyValue = String.fromCharCode(keyCode);
      if (!/^[\d+-]$/.test(keyValue) && keyCode !== 8 && keyCode !== 46 && keyCode !== 37 && keyCode !== 39 && keyCode !== 32 && keyCode !== 43 && keyCode !== 45) {
        event.preventDefault();
      }
    },
    showBillingDetails(row) {
      // Make axios call to fetch billing details for this specific contract line
      axios.get(route('modules.contratti.dettagli-fatturazione', row.RowId))
        .then((response) => {
          this.billingDetails = response.data;
          //this.showBillingDetailsSection = true;
          this.BillingDetailRef = row;
          this.sections.billingDetails = true;
          // Reset local modifications when loading new data
          this.localBillingDetails = [];
          this.localBillingDetailsInitialized = false;
          
          // Scroll to the billing details section
          /*this.$nextTick(() => {
            const billingSection = document.querySelector('.billing-details-section');
            if (billingSection) {
              billingSection.scrollIntoView({ behavior: 'smooth' });
            }
          });*/
        })
        .catch((error) => {
          console.error('Errore durante il caricamento dei dettagli di fatturazione:', error);
          // You might want to show a user-friendly error message here
        });
    },
    padAmountDecimals(value) {
      const numValue = parseFloat(value) || 0;
      const decimals = (numValue.toString().split('.')[1] || '').length;
      return numValue.toFixed(decimals < 2 ? 2 : decimals);
    },
    editBillingDetail(index) {
      // Assicurati che le modifiche locali siano inizializzate
      if (!this.localBillingDetailsInitialized) {
        this.localBillingDetails = [...this.billingDetails];
        this.localBillingDetailsInitialized = true;
      }
      
      this.editingBillingDetailIndex = index;
      const detail = this.currentBillingDetails[index];
      
      this.billingDetailForm = {
        InstallmentNo: detail.InstallmentNo,
        InternalOrdNo: detail.InternalOrdNo || '',
        Amount: this.padAmountDecimals(detail.Amount),
        DueDate: detail.DueDate ? parseISO(detail.DueDate) : null,
        Invoiced: detail.Invoiced === '1' || detail.Invoiced === true
      };
      
      this.showBillingDetailModal = true;
    },
    closeBillingDetailModal() {
      this.showBillingDetailModal = false;
      this.editingBillingDetailIndex = null;
      this.resetBillingDetailForm();
    },
    resetBillingDetailForm() {
      this.billingDetailForm = {
        InstallmentNo: '',
        InternalOrdNo: '',
        Amount: 0,
        DueDate: null,
        Invoiced: false
      };
    },
    saveBillingDetail() {
      // Assicurati che le modifiche locali siano inizializzate
      if (!this.localBillingDetailsInitialized) {
        this.localBillingDetails = [...this.billingDetails];
        this.localBillingDetailsInitialized = true;
      }
      
      if (this.editingBillingDetailIndex !== null) {
        // Aggiorna il dettaglio esistente
        this.localBillingDetails[this.editingBillingDetailIndex] = {
          ...this.localBillingDetails[this.editingBillingDetailIndex],
          InstallmentNo: this.billingDetailForm.InstallmentNo,
          InternalOrdNo: this.billingDetailForm.InternalOrdNo,
          Amount: this.billingDetailForm.Amount,
          DueDate: this.billingDetailForm.DueDate ? new Date(this.billingDetailForm.DueDate - this.billingDetailForm.DueDate.getTimezoneOffset() * 60000).toISOString().split('T')[0] : null,
          Invoiced: this.billingDetailForm.Invoiced ? '1' : '0'
        };
      }
      
      this.closeBillingDetailModal();
    },
    resetForm() {
      router.visit(window.location.href, { method: 'get' });
    },
  },
  mounted() {
    if (this.contract) {
      this.onClientSelect({'id': this.contract.Customer, 'name': this.contract.CompanyName}, false);
    }
    else if (this.prefillData) {
      if (this.prefillData.Customer) {
        this.form.Customer = this.prefillData.Customer;
        this.onClientSelect(this.prefillData.Customer, false);
      }
      if (this.prefillData.SalePerson) {
        this.form.SalePerson = this.prefillData.SalePerson;
      }
      if (this.prefillData.PaymentCondition) {
        this.form.PaymentCondition = this.prefillData.PaymentCondition;
      }
      if (this.prefillData.ContractPlace) {
        this.form.ContractPlace = this.prefillData.ContractPlace;
      }
      if (this.prefillData.StipulationDate) {
        this.form.StipulationDate = parseISO(this.prefillData.StipulationDate);
      }
      this.form.sourceOrderId = this.prefillData.sourceOrderId;

      const baselineRow = {
        ContractId: null,
        Item: '',
        Description: '',
        StartDate: null,
        EndDate: null,
        ActivationDate: null,
        UoM: 'NR',
        Qty: 1,
        UnitValue: 0,
        CopyCost: 0,
        DiscountFormula: 0,
        RenewalAdjustment: 0,
        TotalAmount: 0,
        Disabled: false,
        BillingPeriod: 0,
        Job: '',
        AlreadyInvoiced: false,
        MagoInvoice: '',
        MagoInvoiceLine: null
      };

      this.localContractLines = (this.prefillData.lines || []).map(line => ({
        ...baselineRow,
        ...line,
        TotalAmount: (parseFloat(line.Qty) || 0) * (parseFloat(line.UnitValue) || 0)
      }));
      this.localContractLinesInitialized = true;
    }
    this.$watch(
      () => [this.lineForm.Qty, this.lineForm.UnitValue, this.lineForm.DiscountFormula],
      ([qty, price, discountFormula]) => {
        this.lineForm.TotalAmount = this.formatNumber(this.calculateDiscountedAmount(parseFloat(qty * price), discountFormula).toFixed(2));
      },
      { immediate: true }
    );
  },
  watch: {
    'contract.contractLines': {
      handler(newContractLines) {
        // Quando il contratto viene aggiornato dal server, resetta le modifiche locali
        // così la computed property userà i nuovi dati dal server, ma solo se non ci sono stati errori
        this.localContractLines = [];
        this.localContractLinesInitialized = false;

        this.localBillingDetails = [];
        this.localBillingDetailsInitialized = false;
      },
      deep: true
    }
  }
};
</script>

<style scoped>
.modal-content {
  overflow-y: auto;
  scrollbar-width: thin;
}

/* Quando la modale è aperta, impedisce lo scroll del body */
body:has(.fixed.inset-0.flex.items-center.justify-center.z-50) {
  overflow: hidden;
}

/* Stile per le scrollbar della modale */
.modal-content::-webkit-scrollbar {
  width: 6px;
}

.modal-content::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.modal-content::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 10px;
}

.modal-content::-webkit-scrollbar-thumb:hover {
  background: #555;
}
</style>